import { useEffect, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { useAuth } from "@/contexts/AuthContext";
import { ticketService } from "@/services/ticket.service";
import type {
  TicketDetail,
  TicketMessage,
  TicketStatus,
  TicketStorageSummary,
  TicketSummary,
  TicketType,
} from "@/types/ticket.types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { TicketAttachmentPreview } from "@/components/tickets/TicketAttachmentPreview";
import { localizeTicketMessageText } from "@/components/tickets/ticketMessageLocalization";
import { HardDrive, Trash2, MessagesSquare, Bug, Lightbulb, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 30;
const MAX_FILES = 5;
const MESSAGE_COLLAPSE_LENGTH = 320;
const MESSAGE_BUBBLE_MAX_WIDTH = "min(85%, 38rem)";

type ReplyDraft = { message: string; attachments: File[] };

const EMPTY_REPLY_DRAFT: ReplyDraft = { message: "", attachments: [] };
const toAttachmentKey = (file: File) => `${file.name}:${file.size}:${file.lastModified}`;

const mergeAttachments = (current: File[], incoming: File[]) => {
  const seen = new Set(current.map(toAttachmentKey));
  const merged = [...current];
  for (const file of incoming) {
    const key = toAttachmentKey(file);
    if (seen.has(key)) continue;
    merged.push(file);
    seen.add(key);
  }
  return merged.slice(0, MAX_FILES);
};

const formatBytes = (value: number) => {
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(2)} MB`;
};

const statusBadgeVariant = (status: TicketStatus): "default" | "secondary" | "outline" =>
  status === "OPEN" ? "default" : status === "IN_PROGRESS" ? "secondary" : "outline";

const ticketTypeBadgeClass = (type: TicketType) => {
  if (type === "BUG") return "border-destructive/40 bg-destructive/10 text-destructive";
  if (type === "FEATURE") return "border-primary/40 bg-primary/10 text-primary";
  return "border-secondary/40 bg-secondary/10 text-secondary-foreground";
};

const ticketTypeIcon = (type: TicketType) => {
  if (type === "BUG") return Bug;
  if (type === "FEATURE") return Lightbulb;
  return CheckCircle2;
};
const TICKET_META_BADGE_CLASS = "inline-flex h-9 items-center px-3 text-sm";

const keepScrollPositionAfterFileSelection = () => {
  if (typeof window === "undefined") return;
  const left = window.scrollX;
  const top = window.scrollY;
  window.requestAnimationFrame(() => {
    window.scrollTo({ left, top, behavior: "auto" });
  });
};

export function TicketManagementPanel() {
  const { t } = useApp();
  const { user } = useAuth();
  const [tickets, setTickets] = useState<TicketSummary[]>([]);
  const [summary, setSummary] = useState<TicketStorageSummary | null>(null);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | "ALL">("ALL");
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [loadingDetailIds, setLoadingDetailIds] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [expandedMessageIds, setExpandedMessageIds] = useState<Set<string>>(new Set());
  const [expandedTicketIds, setExpandedTicketIds] = useState<Set<string>>(new Set());
  const [ticketDetailsById, setTicketDetailsById] = useState<Record<string, TicketDetail>>({});
  const [replyDraftsByTicketId, setReplyDraftsByTicketId] = useState<Record<string, ReplyDraft>>({});

  const getReplyDraft = (ticketId: string): ReplyDraft => replyDraftsByTicketId[ticketId] ?? EMPTY_REPLY_DRAFT;

  const toggleMessageExpanded = (messageId: string) => {
    setExpandedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) next.delete(messageId);
      else next.add(messageId);
      return next;
    });
  };

  const toCollapsedMessage = (value: string) =>
    value.length <= MESSAGE_COLLAPSE_LENGTH ? value : `${value.slice(0, MESSAGE_COLLAPSE_LENGTH).trimEnd()}...`;

  const isOwnMessage = (message: TicketMessage) => !!user?.id && message.senderId === user.id;
  const isSystemMessage = (message: TicketMessage) => message.senderType === "SYSTEM";

  const loadSummary = async () => {
    try {
      setSummary(await ticketService.getStorageSummary());
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    }
  };

  const loadTickets = async (status: TicketStatus | "ALL") => {
    setIsLoadingList(true);
    try {
      const response = await ticketService.getAdminTickets({
        page: 0,
        size: PAGE_SIZE,
        status: status === "ALL" ? undefined : status,
      });
      setTickets(response.items);
      const ticketIds = new Set(response.items.map((ticket) => ticket.id));
      setExpandedTicketIds((prev) => {
        const next = new Set<string>();
        for (const ticket of response.items) {
          if (prev.has(ticket.id) || ticket.status !== "CLOSED") next.add(ticket.id);
        }
        return next;
      });
      setTicketDetailsById((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([ticketId]) => ticketIds.has(ticketId)))
      );
      setReplyDraftsByTicketId((prev) =>
        Object.fromEntries(Object.entries(prev).filter(([ticketId]) => ticketIds.has(ticketId)))
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsLoadingList(false);
    }
  };

  const loadDetail = async (ticketId: string) => {
    setLoadingDetailIds((prev) => new Set(prev).add(ticketId));
    try {
      const detail = await ticketService.getAdminTicket(ticketId);
      setTicketDetailsById((prev) => ({ ...prev, [ticketId]: detail }));
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setLoadingDetailIds((prev) => {
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
    }
  };

  useEffect(() => {
    void loadSummary();
    void loadTickets(statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  useEffect(() => {
    tickets.forEach((ticket) => {
      if (!expandedTicketIds.has(ticket.id)) return;
      if (ticketDetailsById[ticket.id]) return;
      if (loadingDetailIds.has(ticket.id)) return;
      void loadDetail(ticket.id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, expandedTicketIds, ticketDetailsById, loadingDetailIds]);

  const refreshAll = async () => Promise.all([loadSummary(), loadTickets(statusFilter)]);
  const refreshTicket = async (ticketId: string) => {
    await Promise.all([loadSummary(), loadTickets(statusFilter)]);
    if (expandedTicketIds.has(ticketId)) await loadDetail(ticketId);
  };

  const toggleTicketExpanded = (ticketId: string) => {
    let shouldLoad = false;
    setExpandedTicketIds((prev) => {
      const next = new Set(prev);
      if (next.has(ticketId)) next.delete(ticketId);
      else {
        next.add(ticketId);
        shouldLoad = true;
      }
      return next;
    });
    if (shouldLoad && !ticketDetailsById[ticketId] && !loadingDetailIds.has(ticketId)) void loadDetail(ticketId);
  };

  const setReplyMessage = (ticketId: string, message: string) =>
    setReplyDraftsByTicketId((prev) => ({ ...prev, [ticketId]: { ...getReplyDraft(ticketId), message } }));

  const handleReplyAttachments = (ticketId: string, files: FileList | null) => {
    if (!files) return;
    setReplyDraftsByTicketId((prev) => {
      const current = prev[ticketId] ?? EMPTY_REPLY_DRAFT;
      const merged = mergeAttachments(current.attachments, Array.from(files));
      if (current.attachments.length + files.length > MAX_FILES) toast.error(t("account.ticketAttachmentsLimit"));
      return { ...prev, [ticketId]: { ...current, attachments: merged } };
    });
  };

  const removeReplyAttachment = (ticketId: string, index: number) =>
    setReplyDraftsByTicketId((prev) => ({
      ...prev,
      [ticketId]: { ...getReplyDraft(ticketId), attachments: getReplyDraft(ticketId).attachments.filter((_, i) => i !== index) },
    }));

  const clearReplyAttachments = (ticketId: string) =>
    setReplyDraftsByTicketId((prev) => ({ ...prev, [ticketId]: { ...getReplyDraft(ticketId), attachments: [] } }));

  const handleReply = async (ticketId: string) => {
    const detail = ticketDetailsById[ticketId];
    const draft = getReplyDraft(ticketId);
    if (!detail) return;
    if (!draft.message.trim() && draft.attachments.length === 0) return toast.error(t("account.ticketMessageOrAttachmentRequired"));
    setIsSaving(true);
    try {
      const updated = await ticketService.createAdminTicketMessage(
        detail.id,
        { message: draft.message.trim() || undefined },
        draft.attachments
      );
      setTicketDetailsById((prev) => ({ ...prev, [ticketId]: updated }));
      setReplyDraftsByTicketId((prev) => ({ ...prev, [ticketId]: { ...EMPTY_REPLY_DRAFT } }));
      setExpandedMessageIds(new Set());
      await loadTickets(statusFilter);
      await loadSummary();
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleStatus = async (ticketId: string) => {
    const detail = ticketDetailsById[ticketId];
    if (!detail) return;
    setIsSaving(true);
    try {
      const payload: { status: TicketStatus } =
        detail.status === "CLOSED"
          ? { status: "OPEN" }
          : { status: "CLOSED" };
      const updated = await ticketService.updateAdminTicketStatus(detail.id, payload);
      setTicketDetailsById((prev) => ({ ...prev, [ticketId]: updated }));
      setExpandedMessageIds(new Set());
      await loadTickets(statusFilter);
      await loadSummary();
      toast.success(t("common.saved"));
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClosedTicket = async (ticketId: string) => {
    const detail = ticketDetailsById[ticketId];
    if (!detail || detail.status !== "CLOSED") return;
    setIsSaving(true);
    try {
      await ticketService.deleteClosedTicket(detail.id);
      setExpandedTicketIds((prev) => {
        const next = new Set(prev);
        next.delete(ticketId);
        return next;
      });
      setTicketDetailsById((prev) => Object.fromEntries(Object.entries(prev).filter(([id]) => id !== ticketId)));
      toast.success(t("common.saved"));
      await loadTickets(statusFilter);
      await loadSummary();
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAttachment = async (ticketId: string, attachmentId: string) => {
    const detail = ticketDetailsById[ticketId];
    if (!detail) return;
    setIsSaving(true);
    try {
      await ticketService.deleteTicketAttachment(detail.id, attachmentId);
      await loadDetail(ticketId);
      setExpandedMessageIds(new Set());
      await loadTickets(statusFilter);
      await loadSummary();
    } catch (error: any) {
      toast.error(error?.response?.data?.message ?? t("common.genericError"));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><HardDrive className="w-5 h-5" />{t("admin.ticketStorageSummaryTitle")}</CardTitle>
          <CardDescription>{t("admin.ticketStorageSummaryDescription")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {summary ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                <div className="rounded-lg border border-border p-3 text-sm"><p className="text-secondary">{t("admin.ticketStorageTotal")}</p><p className="font-semibold">{formatBytes(summary.totalTicketStorageBytes)}</p></div>
                <div className="rounded-lg border border-border p-3 text-sm"><p className="text-secondary">{t("admin.ticketAttachmentTotal")}</p><p className="font-semibold">{formatBytes(summary.totalAttachmentBytes)}</p></div>
                <div className="rounded-lg border border-border p-3 text-sm"><p className="text-secondary">{t("admin.ticketTotalItems")}</p><p className="font-semibold">{summary.totalTickets} / {summary.totalMessages} / {summary.totalAttachments}</p></div>
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium">{t("admin.ticketTopStorage")}</p>
                {summary.topTicketsByAttachmentStorage.length === 0 ? <p className="text-sm text-secondary">{t("admin.systemActivityEmpty")}</p> : summary.topTicketsByAttachmentStorage.map((item) => (
                  <div key={item.ticketId} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm">
                    <span className="min-w-0 flex-1 break-words pr-3">{item.title}</span>
                    <span className="break-words text-secondary [overflow-wrap:anywhere]">{item.attachmentCount} / {formatBytes(item.attachmentsBytesTotal)}</span>
                  </div>
                ))}
              </div>
            </>
          ) : <p className="text-sm text-secondary">{t("common.loading")}</p>}
        </CardContent>
      </Card>

      <Card className="stat-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><MessagesSquare className="w-5 h-5" />{t("admin.tickets")}</CardTitle>
          <CardDescription>{t("admin.ticketInboxDescription")}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as TicketStatus | "ALL")}>
              <SelectTrigger className="w-full sm:w-[220px]"><SelectValue placeholder={statusFilter} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">{t("account.ticketFilterAll")}</SelectItem>
                <SelectItem value="OPEN">{t("account.ticketStatusOPEN")}</SelectItem>
                <SelectItem value="IN_PROGRESS">{t("account.ticketStatusIN_PROGRESS")}</SelectItem>
                <SelectItem value="CLOSED">{t("account.ticketStatusCLOSED")}</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => void refreshAll()}>{t("admin.systemLogsRefresh")}</Button>
          </div>

          {isLoadingList ? <p className="text-sm text-secondary">{t("common.loading")}</p> : tickets.length === 0 ? (
            <p className="text-sm text-secondary">{t("account.ticketListEmpty")}</p>
          ) : (
            <div className="space-y-3">
              {tickets.map((ticket) => {
                const isExpanded = expandedTicketIds.has(ticket.id);
                const detail = ticketDetailsById[ticket.id];
                const isDetailLoading = loadingDetailIds.has(ticket.id);
                const replyDraft = getReplyDraft(ticket.id);
                const effectiveStatus = detail?.status ?? ticket.status;
                return (
                  <div key={ticket.id} className="rounded-lg border border-border p-3">
                    <div className="w-full rounded-md transition hover:bg-accent/10">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <button
                          type="button"
                          className="min-w-0 flex-1 rounded-md p-1 -m-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                          onClick={() => toggleTicketExpanded(ticket.id)}
                          aria-expanded={isExpanded}
                        >
                          <p className="min-w-0 flex-1 break-words font-medium leading-tight">{ticket.title}</p>
                          <p className="mt-1 break-words text-xs text-secondary">{ticket.reporterUsername}</p>
                          <p className="mt-1 break-words text-xs text-secondary [overflow-wrap:anywhere]">{localizeTicketMessageText(ticket.lastMessagePreview, t)}</p>
                          <p className="mt-2 text-xs text-secondary">{ticket.messageCount} {t("account.ticketMessagesShort")} / {ticket.attachmentCount} {t("account.ticketAttachmentsShort")} / {formatBytes(ticket.attachmentsBytesTotal)}</p>
                        </button>
                        <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={isSaving}
                            onClick={() => void handleToggleStatus(ticket.id)}
                          >
                            {effectiveStatus === "CLOSED"
                              ? t("account.ticketStatusOPEN")
                              : t("account.ticketCloseAction")}
                          </Button>
                          <Badge className={`${ticketTypeBadgeClass(ticket.type)} ${TICKET_META_BADGE_CLASS}`} variant="outline"><span className="inline-flex items-center gap-1">{(() => { const Icon = ticketTypeIcon(ticket.type); return <Icon className="h-3.5 w-3.5" />; })()}{t(`account.ticketType${ticket.type}`)}</span></Badge>
                          <Badge className={TICKET_META_BADGE_CLASS} variant={statusBadgeVariant(effectiveStatus)}>{t(`account.ticketStatus${effectiveStatus}`)}</Badge>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 rounded-lg border border-border p-3">
                        {isDetailLoading || !detail ? <p className="text-sm text-secondary">{t("common.loading")}</p> : (
                          <div className="space-y-4">
                            <div className="max-h-[320px] space-y-3 overflow-x-hidden overflow-y-auto pr-1">
                              {detail.messages.map((message) => {
                                const localizedMessage = localizeTicketMessageText(message.message, t);
                                if (isSystemMessage(message)) {
                                  return (
                                    <div key={message.id} className="flex items-center gap-3 py-1">
                                      <span className="h-px flex-1 bg-border" />
                                      <p className="max-w-[80%] break-words text-center text-xs font-medium text-secondary [overflow-wrap:anywhere]">
                                        {localizedMessage || t("account.messagesFromSystem")}
                                      </p>
                                      <span className="h-px flex-1 bg-border" />
                                    </div>
                                  );
                                }
                                return (
                                  <div key={message.id} className={`flex ${isOwnMessage(message) ? "justify-end" : "justify-start"}`}>
                                    <div className={`min-w-0 w-fit max-w-full rounded-lg border p-3 ${isOwnMessage(message) ? "border-primary/40 bg-primary/5" : "border-border bg-background"}`} style={{ maxWidth: MESSAGE_BUBBLE_MAX_WIDTH }}>
                                      <div className={`flex min-w-0 flex-wrap items-center gap-2 ${isOwnMessage(message) ? "justify-end text-right" : "justify-between"}`}>
                                        <p className="min-w-0 break-words text-sm font-medium [overflow-wrap:anywhere]">{message.senderUsername ?? (message.senderType === "SYSTEM" ? t("account.messagesFromSystem") : message.senderType)}</p>
                                        <p className="text-xs text-secondary">{new Date(message.createdAt).toLocaleString()}</p>
                                      </div>
                                      {localizedMessage && (
                                        <>
                                          <p className="mt-2 whitespace-pre-wrap break-all text-sm [overflow-wrap:anywhere] [word-break:break-word]">{expandedMessageIds.has(message.id) ? localizedMessage : toCollapsedMessage(localizedMessage)}</p>
                                          {localizedMessage.length > MESSAGE_COLLAPSE_LENGTH && (
                                            <Button type="button" size="sm" variant="ghost" className="mt-1 h-7 px-2" onClick={() => toggleMessageExpanded(message.id)}>{expandedMessageIds.has(message.id) ? t("common.showLess") : t("common.showMore")}</Button>
                                          )}
                                        </>
                                      )}
                                      {message.attachments.length > 0 && (
                                        <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                          {message.attachments.map((attachment) => (
                                            <TicketAttachmentPreview key={attachment.id} ticketId={detail.id} attachment={attachment} fetchBlob={ticketService.getAdminAttachmentBlob} onOpen={() => ticketService.openAdminAttachment(detail.id, attachment.id)} t={t} extraAction={<Button size="sm" variant="destructive" className="h-7 px-2" onClick={() => void handleDeleteAttachment(ticket.id, attachment.id)}>{t("common.delete")}</Button>} />
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>

                            <div className="space-y-2 rounded-lg border border-border p-3">
                              <Label>{t("admin.userMessagesBody")}</Label>
                              <Textarea value={replyDraft.message} maxLength={5000} onChange={(event) => setReplyMessage(ticket.id, event.target.value)} placeholder={t("admin.userMessagesBodyPlaceholder")} />
                              <Input
                                type="file"
                                accept="image/png,image/jpeg,image/gif"
                                multiple
                                onChange={(event) => {
                                  handleReplyAttachments(ticket.id, event.target.files);
                                  event.currentTarget.value = "";
                                  keepScrollPositionAfterFileSelection();
                                }}
                              />
                              {replyDraft.attachments.length > 0 && (
                                <div className="space-y-2">
                                  <div className="flex flex-wrap items-center justify-between gap-2">
                                    <p className="text-xs text-secondary">{replyDraft.attachments.length} {t("account.ticketFilesSelected")}</p>
                                    <Button type="button" size="sm" variant="ghost" className="h-7 px-2" onClick={() => clearReplyAttachments(ticket.id)}>{t("common.delete")}</Button>
                                  </div>
                                  <div className="space-y-1">
                                    {replyDraft.attachments.map((file, index) => (
                                      <div key={toAttachmentKey(file)} className="flex flex-wrap items-center gap-2 text-xs">
                                        <span className="min-w-0 flex-1 break-all">{file.name}</span>
                                        <span className="text-secondary">{formatBytes(file.size)}</span>
                                        <Button type="button" size="sm" variant="outline" className="h-7 px-2" onClick={() => removeReplyAttachment(ticket.id, index)}>{t("common.delete")}</Button>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                              <div className="flex flex-wrap items-center justify-between gap-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <Button variant="outline" onClick={() => { void refreshTicket(ticket.id); }}>{t("admin.systemLogsRefresh")}</Button>
                                  {detail.status === "CLOSED" && (
                                    <Button variant="destructive" disabled={isSaving} onClick={() => void handleDeleteClosedTicket(ticket.id)}>
                                      <Trash2 className="mr-2 h-4 w-4" />{t("admin.ticketDeleteClosed")}
                                    </Button>
                                  )}
                                </div>
                                <Button className="btn-primary w-full sm:w-auto" disabled={isSaving} onClick={() => void handleReply(ticket.id)}>{t("admin.userMessagesSend")}</Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
